import { ButtonProtected, DateFormatter, IntegrationCard } from '@components'

import { Button, Color, Flex, Icon, IconButton, Spacing, Text, useModal } from '@billy/design-system'
import isToday from 'date-fns/isToday'
import truncate from 'lodash/truncate'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useUpdateScoutConnection } from '@views/receipts/hooks/useUpdateScoutConnection'

import { ModalId } from '../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { Scope } from '../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../hooks'
import { formatDate } from '../../../../../../utils'
import { ScoutProvider } from '../../../../enums/scoutProvider'
import { useCreateScoutConsentUrl } from '../../../../hooks/useCreateScoutConsentUrl'
import { CreateScoutConsentUrlResponse } from '../../../../query-api'
import { ScoutConnection } from '../../../../types/scoutConnection'
import { providersImage } from '../../constants/providerImage'
import { providersTranslationKeys } from '../../constants/providerTranslationKey'

interface ScoutConnectionCardProps {
  provider: ScoutProvider
  connection?: ScoutConnection
  onConnectionDeleted: (connection: ScoutConnection) => void
}

export const ScoutConnectionCard = ({
  provider,
  connection,
  onConnectionDeleted,
}: ScoutConnectionCardProps): ReactElement => {
  const { track } = useSegment()
  const { t } = useTranslation()
  const { bookkeepingBankAccounts } = useBookkeepingBankAccounts()
  const queryClient = useQueryClient()
  const { update: updateScoutConnection } = useUpdateScoutConnection({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.ScoutConnections)
    },
  })
  const { open: openDeleteConfirmationModal } = useModal(ModalId.ScoutConnectionDeletionModal)
  const { open: openScoutConnectionImapModal } = useModal(ModalId.ScoutConnectionImapModal)

  const { create: createGoogleConsentUrl } = useCreateScoutConsentUrl({
    onSuccess: (response: CreateScoutConsentUrlResponse) => {
      window.open(response[ScoutProvider.Google], '_self')
    },
  })

  const { create: createMicrosoftConsentUrl } = useCreateScoutConsentUrl({
    onSuccess: (response: CreateScoutConsentUrlResponse) => {
      window.open(response[ScoutProvider.Microsoft], '_self')
    },
  })

  const handleConnectButtonClick = useCallback(
    (scoutProvider: ScoutProvider) => () => {
      track('Scout Connected (Client)', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        connection_type: scoutProvider,
      })
      const accountIds = bookkeepingBankAccounts?.map(({ id }) => id) ?? []
      switch (scoutProvider) {
        case ScoutProvider.Google: {
          createGoogleConsentUrl(accountIds)
          break
        }
        case ScoutProvider.Microsoft: {
          createMicrosoftConsentUrl(accountIds)
          break
        }
        case ScoutProvider.Imap: {
          setTimeout(() => {
            openScoutConnectionImapModal()
          }, 100)
          break
        }
      }
    },
    [bookkeepingBankAccounts, createGoogleConsentUrl, createMicrosoftConsentUrl, openScoutConnectionImapModal, track],
  )

  const handleDisconnectButtonClick = useCallback(
    (connectionToDisconnect: ScoutConnection) => () => {
      track('Scout Disconnected (Client)', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        connection_type: connectionToDisconnect.provider,
      })
      onConnectionDeleted(connectionToDisconnect)
      openDeleteConfirmationModal()
    },
    [openDeleteConfirmationModal, track, onConnectionDeleted],
  )

  const handleSyncNowButtonClick = useCallback(
    (connectionToSync: ScoutConnection) => () => {
      updateScoutConnection({ connectionId: connectionToSync.id, syncNow: true })
    },
    [updateScoutConnection],
  )

  const subTitle = connection?.email
    ? truncate(connection?.email, { length: 17, omission: '...' })
    : t(providersTranslationKeys[provider].description)

  return (
    <IntegrationCard key={provider}>
      <IntegrationCard.Header
        title={t(providersTranslationKeys[provider].title)}
        subtitle={subTitle}
        image={providersImage[provider]}
        sideActions={
          <Flex alignItems="center" justifyContent="flex-end">
            {connection ? (
              <Button variant="outlined" onClick={handleDisconnectButtonClick(connection)}>
                {t('receipts.side_panel.disconnect')}
              </Button>
            ) : (
              <ButtonProtected
                trackingContext={TrackingContext.Scout}
                variant="filled"
                onClick={handleConnectButtonClick(provider)}
                scopes={Scope.ScoutWrite}
              >
                {t('receipts.side_panel.connect')}
              </ButtonProtected>
            )}
          </Flex>
        }
      />
      {connection && (
        <>
          <IntegrationCard.Divider />
          <IntegrationCard.Footer>
            {connection.isProcessing ? (
              <Flex alignItems="center" gap={Spacing.S8} justifyContent="flex-end" width="100%">
                <Text>{t('receipts.side_panel.sync.syncing')}</Text>
                <Icon icon="circleDashed" color={Color.Black} />
              </Flex>
            ) : (
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Text colorVariant="secondary">{t('receipts.side_panel.sync.last_sync')}</Text>
                <Flex alignItems="center" gap={Spacing.S4}>
                  {connection.lastFetchedAt && (
                    <Text>
                      {isToday(new Date(connection.lastFetchedAt)) ? (
                        t('today')
                      ) : (
                        <DateFormatter value={connection.lastFetchedAt} />
                      )}
                      {`, ${formatDate(new Date(connection.lastFetchedAt), 'HH:mm')}`}
                    </Text>
                  )}
                  <IconButton icon="refresh" onClick={handleSyncNowButtonClick(connection)} size="md"></IconButton>
                </Flex>
              </Flex>
            )}
          </IntegrationCard.Footer>
        </>
      )}
    </IntegrationCard>
  )
}
